// frontend/src/pages/configuracoes/auditoria/DetalhesAuditoriaModal.js

import React, { useEffect, useState } from "react";
import api from "../../../services/api";
import { formatDateWithHour } from "../../../utils/date";

const operacoesLabel = {
    create: "Criação",
    update: "Atualização",
    delete: "Exclusão",
    upsert: "Upsert",
    deleteMany: "Exclusão em massa",
};

const RelacaoTag = ({ id, label }) => (
    <span className="inline-flex items-baseline gap-1.5">
        <span className="font-medium text-stone-700">{label}</span>
        <span className="text-xs text-stone-400">#{id}</span>
    </span>
);

const operacoesNavLabel = {
    create: "criado(s)",
    update: "atualizado(s)",
    upsert: "criado/atualizado(s)",
    delete: "removido(s)",
    deleteMany: "exclusão em massa",
    updateMany: "atualização em massa",
};

const isPlainObject = (v) =>
    v !== null && typeof v === "object" && !Array.isArray(v) && !(v instanceof Date);

const RelOpResumo = ({ relOp }) => {
    const partes = (relOp.operacoes || []).map((op, idx) => {
        if (op.tipo === "connect" || op.tipo === "set") {
            const itens = (op.ids || []).map((id, i) => ({ id, label: op.labels?.[i] ?? null }));
            const verbo = op.tipo === "connect" ? "vinculado(s)" : "definido(s) como";
            return (
                <div key={idx} className="text-sm text-stone-700">
                    <span className="font-medium">{relOp.target}</span> {verbo}:
                    <div className="flex flex-wrap gap-1.5 mt-1">
                        {itens.map((it) => (
                            <span
                                key={it.id}
                                className="inline-flex items-baseline gap-1 bg-stone-100 border border-stone-200 rounded px-2 py-0.5"
                            >
                                <span className="text-stone-700">{it.label || `#${it.id}`}</span>
                                {it.label && <span className="text-xs text-stone-400">#{it.id}</span>}
                            </span>
                        ))}
                    </div>
                </div>
            );
        }
        if (op.tipo === "disconnect") {
            return (
                <div key={idx} className="text-sm text-stone-700">
                    <span className="font-medium">{relOp.target}</span> desvinculado
                    {Array.isArray(op.ids) && op.ids.length ? `: ${op.ids.length} item(ns)` : ""}.
                </div>
            );
        }
        if (op.count != null) {
            return (
                <div key={idx} className="text-sm text-stone-700">
                    <span className="font-medium">{op.count}</span> {relOp.target}{" "}
                    {operacoesNavLabel[op.tipo] || op.tipo}.
                </div>
            );
        }
        return (
            <div key={idx} className="text-sm text-stone-700">
                <span className="font-medium">{relOp.target}</span>:{" "}
                {operacoesNavLabel[op.tipo] || op.tipo}.
            </div>
        );
    });
    return <div className="flex flex-col gap-1">{partes}</div>;
};

const renderValor = (valor) => {
    if (valor === null || valor === undefined) {
        return <span className="italic text-stone-400">vazio</span>;
    }
    if (isPlainObject(valor) && valor.__relOp) {
        return <RelOpResumo relOp={valor} />;
    }
    if (typeof valor === "object") return JSON.stringify(valor, null, 2);
    return String(valor);
};

const renderValorDiff = (valor, label) => {
    if (label != null && valor != null && !(isPlainObject(valor) && valor.__relOp)) {
        return <RelacaoTag id={valor} label={label} />;
    }
    return renderValor(valor);
};

const DetalhesAuditoriaModal = ({ auditoriaId, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");
    const [data, setData] = useState(null);
    const [aba, setAba] = useState("diff");

    useEffect(() => {
        const carregar = async () => {
            setLoading(true);
            setErrorMsg("");
            try {
                const response = await api.get(`/auditoria/${auditoriaId}`);
                setData(response.data);
                if (response.data.operacao === "create") setAba("novos");
                else if (
                    response.data.operacao === "delete" ||
                    response.data.operacao === "deleteMany"
                ) {
                    setAba("antigos");
                } else {
                    setAba("diff");
                }
            } catch (err) {
                console.error("Erro ao carregar detalhe da auditoria:", err);
                setErrorMsg(err.response?.data?.msg || "Erro ao carregar detalhe.");
            } finally {
                setLoading(false);
            }
        };
        carregar();
    }, [auditoriaId]);

    const renderConteudo = () => {
        if (!data) return null;
        if (aba === "diff") {
            const entries = Object.entries(data.diff || {});
            if (entries.length === 0) {
                return (
                    <div className="text-sm text-stone-500 italic">
                        Nenhuma alteração detectada.
                    </div>
                );
            }
            return (
                <table className="min-w-full text-sm border-collapse">
                    <thead>
                        <tr className="bg-stone-50">
                            <th className="px-3 py-2 text-left border border-slate-200 w-1/4">Campo</th>
                            <th className="px-3 py-2 text-left border border-slate-200 w-1/3">Antes</th>
                            <th className="px-3 py-2 text-left border border-slate-200 w-1/3">Depois</th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map(([campo, valores]) => (
                            <tr key={campo} className="align-top">
                                <td className="px-3 py-2 border border-slate-200 font-medium text-stone-700">
                                    {campo}
                                </td>
                                <td className="px-3 py-2 border border-slate-200 bg-red-50/40 break-words whitespace-pre-wrap">
                                    {renderValorDiff(valores.antes, valores.antesLabel)}
                                </td>
                                <td className="px-3 py-2 border border-slate-200 bg-emerald-50/40 break-words whitespace-pre-wrap">
                                    {renderValorDiff(valores.depois, valores.depoisLabel)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            );
        }
        if (aba === "antigos") {
            return (
                <pre className="text-xs whitespace-pre-wrap break-all bg-stone-50 p-3 rounded border border-stone-200 max-h-[60vh] overflow-auto">
                    {data.dadosAntigos
                        ? JSON.stringify(data.dadosAntigos, null, 2)
                        : "(sem dados antigos)"}
                </pre>
            );
        }
        if (aba === "novos") {
            return (
                <pre className="text-xs whitespace-pre-wrap break-all bg-stone-50 p-3 rounded border border-stone-200 max-h-[60vh] overflow-auto">
                    {data.dadosNovos
                        ? JSON.stringify(data.dadosNovos, null, 2)
                        : "(sem dados novos)"}
                </pre>
            );
        }
        return null;
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                    <div>
                        <h2 className="text-lg font-semibold text-stone-800">
                            Detalhe do Evento de Auditoria
                        </h2>
                        {data && (
                            <p className="text-sm text-stone-500 mt-1">
                                {data.tabela} · ID #{data.registroId} · {operacoesLabel[data.operacao] || data.operacao} ·{" "}
                                {formatDateWithHour(data.data)}
                                {data.usuario && (
                                    <>
                                        {" · "}
                                        <span>{data.usuario.nome}</span>
                                    </>
                                )}
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-stone-500 hover:text-stone-800 text-2xl leading-none"
                        aria-label="Fechar"
                    >
                        ×
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {loading ? (
                        <div className="text-center text-stone-600 py-10">Carregando...</div>
                    ) : errorMsg ? (
                        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-3 text-sm">
                            {errorMsg}
                        </div>
                    ) : (
                        <>
                            <div className="flex gap-2 mb-4 border-b border-slate-200">
                                <TabButton ativo={aba === "diff"} onClick={() => setAba("diff")}>
                                    Diferenças
                                </TabButton>
                                <TabButton ativo={aba === "antigos"} onClick={() => setAba("antigos")}>
                                    Dados Anteriores
                                </TabButton>
                                <TabButton ativo={aba === "novos"} onClick={() => setAba("novos")}>
                                    Dados Novos
                                </TabButton>
                            </div>
                            <div className="overflow-x-auto">{renderConteudo()}</div>
                        </>
                    )}
                </div>

                <div className="px-6 py-3 border-t border-slate-200 flex justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md"
                    >
                        Fechar
                    </button>
                </div>
            </div>
        </div>
    );
};

const TabButton = ({ ativo, children, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            ativo
                ? "border-stone-700 text-stone-800"
                : "border-transparent text-stone-500 hover:text-stone-700"
        }`}
    >
        {children}
    </button>
);

export default DetalhesAuditoriaModal;
